"use client";
import { useEffect, useRef, useState } from "react";
import { bodyStyle, navLinkStyle, navSection } from "@/styles/cva/home.cva";
import SearchBar from "./Search/SearchBar";
import { StateTable } from "./tables/StateTable";

interface StateData {
  usa: any[];
  uk: any[];
}

interface BodyProps {
  states: StateData;
  category: string;
}

export default function Body({ states, category }: BodyProps) {
  const isAQ = category === "aq";
  const categoryText = isAQ ? "Air quality" : "Pollen";
  const [activeSection, setActiveSection] = useState<string>("");
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const getHeadingText = (country: string) => (
    <span className="text-white">
      {isAQ ? "Air pollution levels across " : "Explore pollen counts in "}
      <span className="text-primary-50">{country.toUpperCase()}</span> here
    </span>
  );

  useEffect(() => {
    const handleScroll = () => {
      let closest = "";
      let closestOffset = Infinity;

      Object.entries(sectionRefs.current).forEach(([key, el]) => {
        if (el) {
          const rect = el.getBoundingClientRect();
          const offset = Math.abs(rect.top - 100); // Adjust for your sticky nav height
          if (offset < closestOffset && rect.top < window.innerHeight) {
            closest = key;
            closestOffset = offset;
          }
        }
      });

      if (closest && closest !== activeSection) {
        setActiveSection(closest);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Initial check on mount

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [states, activeSection]);

  return (
    <div className={bodyStyle()}>
      <SearchBar />

      {/* Sticky Nav Bar */}
      <div className={navSection()}>
        <span>{categoryText} in:</span>
        {Object.keys(states).map((country) => (
          <a
            key={country}
            href={`#${country}`}
            className={`${navLinkStyle()} ${
              activeSection === country
                ? "text-white font-bold"
                : "text-[#7E7F82]"
            }`}
          >
            {country.toUpperCase()}
          </a>
        ))}
      </div>

      {/* Content Sections */}
      <div className="w-full flex flex-col gap-y-4 -mt-6">
        {Object.entries(states).map(([country, list]) => (
          <div
            key={country}
            id={country}
            ref={(el: any) => (sectionRefs.current[country] = el)}
            className="scroll-section-mb lg:scroll-section"
          >
            <StateTable
              country={country}
              list={list}
              heading={getHeadingText(country)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
